/**
 * The point at which a work order has sat in one status long enough that the
 * person who owes the next step is reminded. Shared so the list badge and the
 * reminder job agree on what "stalled" means.
 */
export type StallStage = 'procure' | 'schedule' | 'start' | 'complete' | 'close' | 'usps_decision';

export interface StallSettings {
  scheduleAfterBusinessDays: number;
  completeAfterDays: number;
  closeAfterDays: number;
  uspsDecisionAfterDays: number;
  repeatEveryDays: number;
}

export const DEFAULT_STALL_SETTINGS: StallSettings = {
  scheduleAfterBusinessDays: 2,
  completeAfterDays: 5,
  closeAfterDays: 5,
  uspsDecisionAfterDays: 5,
  repeatEveryDays: 3,
};

export const STALL_STAGE_LABELS: Record<StallStage, string> = {
  procure: 'Overdue: record PR/PO',
  schedule: 'Overdue: schedule the work',
  start: 'Overdue: start work',
  complete: 'Overdue: record completion',
  close: 'Overdue: close',
  usps_decision: 'Overdue: USPS decision',
};

/** Weekdays elapsed between two instants, ignoring holidays. */
export function businessDaysBetween(from: Date, to: Date): number {
  if (to <= from) return 0;
  let count = 0;
  const cursor = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()));
  const end = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate()));
  while (cursor < end) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    const day = cursor.getUTCDay();
    if (day !== 0 && day !== 6) count += 1;
  }
  return count;
}

const DAY_MS = 86_400_000;

export function stalledStage(
  input: {
    status: string;
    enteredAt: string | Date;
    scheduledDate: string | null;
    /** When the PR/PO or P-card was recorded; null while procurement is still open. */
    procurementCompletedAt?: string | Date | null;
  },
  settings: StallSettings,
  now: Date = new Date(),
): StallStage | null {
  const enteredAt = new Date(input.enteredAt);
  const days = (now.getTime() - enteredAt.getTime()) / DAY_MS;
  const overdueBusinessDays = (since: Date): boolean =>
    businessDaysBetween(since, now) >= settings.scheduleAfterBusinessDays &&
    (now.getTime() - since.getTime()) / DAY_MS >= settings.scheduleAfterBusinessDays;
  switch (input.status) {
    case 'approved':
      return overdueBusinessDays(enteredAt) ? 'schedule' : null;
    case 'procurement': {
      if (!input.procurementCompletedAt) return overdueBusinessDays(enteredAt) ? 'procure' : null;
      return overdueBusinessDays(new Date(input.procurementCompletedAt)) ? 'schedule' : null;
    }
    case 'repair_scheduled': {
      if (!input.scheduledDate) return null;
      // The scheduled day itself is not overdue; the morning after it is.
      const scheduled = new Date(`${input.scheduledDate.slice(0, 10)}T00:00:00Z`);
      return now.getTime() - scheduled.getTime() >= DAY_MS ? 'start' : null;
    }
    case 'work_in_progress':
      return days >= settings.completeAfterDays ? 'complete' : null;
    case 'completed':
      return days >= settings.closeAfterDays ? 'close' : null;
    case 'awaiting_customer_approval':
      return days >= settings.uspsDecisionAfterDays ? 'usps_decision' : null;
    default:
      return null;
  }
}
